<template>
    <Card v-if="canSwitch">
        <CardHeader>
            <div class="flex items-center gap-2">
                <Repeat class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Switch app type
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent>
            <div class="space-y-5">
                <p class="text-sm text-muted-foreground">
                    You are currently using the
                    <span class="font-medium text-foreground">{{ currentLabel }}</span>
                    version of TOPOL.io.
                </p>

                <ul class="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                    <li>
                        <span class="font-medium text-foreground">Plugin</span>
                        — White-label editor to integrate into platforms, CRMs, and SaaS products.
                        <a
                            href="https://topol.io/tariff-plugin"
                            target="_blank"
                            class="font-medium text-new-blue-500 hover:text-new-blue-600"
                        >
                            Learn more
                        </a>
                    </li>
                    <li>
                        <span class="font-medium text-foreground">PRO</span>
                        — Cloud-based email &amp; landing page editor for individuals and teams.
                        <a
                            href="https://topol.io/tariff-pro"
                            target="_blank"
                            class="font-medium text-new-blue-500 hover:text-new-blue-600"
                        >
                            Learn more
                        </a>
                    </li>
                </ul>

                <label class="flex items-start gap-2 cursor-pointer">
                    <Checkbox v-model="confirmed" class="mt-0.5" />
                    <span class="text-sm text-foreground">
                        Confirm that you want to switch to
                        <span class="font-medium">{{ targetLabel }}</span>.
                        This will change your dashboard and available features.
                    </span>
                </label>

                <div class="flex">
                    <Button
                        variant="destructive"
                        :disabled="!confirmed || switching"
                        @click="dialogOpen = true"
                    >
                        Switch to {{ targetLabel }}
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>

    <Dialog :open="dialogOpen" @update:open="(val) => !val && (dialogOpen = false)">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Switch to {{ targetLabel }}</DialogTitle>
                <DialogDescription>
                    Are you sure you want to switch your dashboard to {{ targetLabel }}?
                    This will change your workspace and available features.
                </DialogDescription>
            </DialogHeader>

            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" :disabled="switching" @click="dialogOpen = false">
                    Cancel
                </Button>
                <Button size="lg" variant="destructive" :disabled="switching" @click="confirmSwitch">
                    Switch to {{ targetLabel }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { Repeat } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import usePermissions from '@/composables/usePermissions';

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const { isOwner } = usePermissions();

const confirmed = ref(false);
const switching = ref(false);
const dialogOpen = ref(false);

// Only personal-team owners on a fully-selected app type (plugin or pro) may switch.
// null and both users are intentionally excluded — the switcher converts plugin↔pro,
// which is meaningless for those states.
const canSwitch = computed(() => {
    const appType = globalProps.value.user.app_type;
    return globalProps.value.team.personal_team
        && isOwner.value
        && (appType === 'plugin' || appType === 'pro');
});

const targetType = computed<'plugin' | 'pro'>(() =>
    globalProps.value.user.app_type === 'plugin' ? 'pro' : 'plugin'
);
const currentLabel = computed(() =>
    globalProps.value.user.app_type === 'plugin' ? 'Plugin' : 'PRO'
);
const targetLabel = computed(() => (targetType.value === 'plugin' ? 'Plugin' : 'PRO'));

const confirmSwitch = () => {
    const target = targetType.value;
    const label = targetLabel.value;

    router.put(
        route('settings.app-type.update'),
        { app_type: target },
        {
            onStart: () => {
                switching.value = true;
            },
            onFinish: () => {
                switching.value = false;
            },
            onSuccess: () => {
                confirmed.value = false;
                dialogOpen.value = false;
                toast.success(`Your dashboard has been switched to ${label}.`);
            },
            onError: (errors) => {
                const message = (Object.values(errors)[0] as string) || 'Failed to switch app type';
                toast.error(message);
            },
        }
    );
};
</script>
